// IIID Parametrics
// ================
//
// This library implements parametric modeling of frame and panel doors and
// similar constructions. The main challenge is to avoid stretching the UV
// coordinates of a mesh while manipulating its overall dimensions. This is
// accomplished mainly through the concept of
// [nine slice scaling](https://en.wikipedia.org/wiki/9-slice_scaling),
// introduced in Adobe Flash. The library uses the terms rail, stile, and panel,
// which have special meaning in [frame and
// panel](https://en.wikipedia.org/wiki/Frame_and_panel) construction.

import {
    Box2,
    BoxGeometry,
    BufferAttribute,
    BufferGeometry,
    Vector2,
    Vector3
} from 'three';


// The AttributeBuilder class inserts data into an underlying typed array.
// The underlying array needs to be constructed with a known capacity and
// the builder will not check if it is about to overrun that capacity, but a
// well designed mesh construction algorithm shouldn't run into runtime
// errors.
// `Type` is the constructor of the TypedArray to use, e.g. Float32Array in
// most cases.
// `itemSize` is 2 for UV attributes, or 3 for positions or normals.
// `count` is the number of items, so the underlying TypedArray has length
// itemSize*count.
class AttributeBuilder {
    constructor(Type, itemSize, count) {
        this.data = new Type(itemSize*count);
        this.itemSize = itemSize;
        this.index = 0;
    }

    // Each item parameter should be an array of numbers  matching the type
    // of the array. The length of each item should match the itemSize with
    // which we were constructed.
    add(...items) {
        const jlen = this.itemSize;
        for(let item of items) {
            for(let j=0; j < jlen; j++){
                this.data[this.index++] = item[j];
            }
        }
    }

    // This is a convenience method for adding a triangle given three
    // points. It's the same as calling .add with three arguments, but has
    // the advantage of explicitly naming its purpose.
    tri(a, b, c) {
        this.add(a, b, c);
    }

    // Given four items, generate two triangles with the same winding order.
    // If the quad is given in counterclockwise order with vertexes a, b,
    // c, and d, then the two triangles generated are abc, and acd.
    // <pre>
    //   d       c
    //    -------
    //    |    /|
    //    |  /  |
    //    |/    |
    //    -------
    //   a       b
    // </pre>
    quad(a, b, c, d) {
        this.add(a, b, c);
        this.add(a, c, d);
    }

    // Inserts a quad, but with reversed winding order. Using this method
    // makes it easier to copy-paste from a similar surface with the
    // opposite facing direction
    rquad(a, b, c, d) {
        this.quad(a, d, c, b)
    }

    // Returns a new BufferAttribute with the underlying data and item size.
    finalize() {
        if(this.i < this.data.length) {
            console.warn('AttributeBuilder finalized but not all data written.', this);
        }
        return new BufferAttribute(this.data, this.itemSize);
    }
}

// Doors
// ------

// This section contains functions related to door construction.

// **buttJointFrame(geometry, dims)**
//
// Returns a BufferGeometry instance representing a butt jointed door frame
// with the given outer dimensions. The stile runs the full height of the
// frame whereas the rail only runs the width minus twice the width of the
// stile.
//
// `geometry` should include BufferGeometry instances named
// `frame_rail_endcap`, `frame_rail_body`, `frame_stile_endcap`, and
// `frame_stile_body`.
export const buttJointFrame = function(geometry, dims){
    var clones = Object();
    for( var name of ['frame_rail_endcap', 'frame_rail_body', 'frame_stile_endcap', 'frame_stile_body']){
        clones[name] = partRemoveOffset(geometry[name].clone());
    };

    var stile_dims = dimensions(clones['frame_stile_body']);
    var rail_length = dims.width - 2*stile_dims.width; // rails run between stiles
    var stile_length = dims.height; // stiles run full height

    var left = stile(clones, stile_length);
    var right = mirror(left, AXIS_X);
    // top is a reserved word in JS, so we use top_rail instead
    var bottom_rail = rail(clones, rail_length);
    var top_rail = mirror(bottom_rail, AXIS_Y);

    var rail_dims = dimensions(bottom_rail);

    right.translate(rail_length + stile_dims.width, 0, 0);
    bottom_rail.translate(stile_dims.width, 0, 0);
    top_rail.translate(stile_dims.width, stile_length - rail_dims.height, 0);

    let result = join([left, right, top_rail, bottom_rail]);
    result.name = 'ButtJointFrame';
    return result;
}

// **miteredFrame(geometry, dims)**
//
// Returns a BufferGeometry instance representing a door frame made with
// miter cuts.
//
// `geometry` should include BufferGeometry instances named
// `frame_rail_endcap`, `frame_rail_body`, `frame_stile_endcap`, and
// `frame_stile_body`.
export const miteredFrame = function(geometry, dims){
    var clones = Object();
    for( var name of ['frame_rail_endcap', 'frame_rail_body', 'frame_stile_endcap', 'frame_stile_body']){
        clones[name] = partRemoveOffset(geometry[name].clone());
    };

    var left = stile(clones, dims.height);
    var right = mirror(left, AXIS_X);

    var bottom_rail = rail(clones, dims.width);
    var top_rail = mirror(bottom_rail, AXIS_Y);

    var stile_dims = dimensions(left);
    right.translate(dims.width - stile_dims.width, 0, 0);

    var rail_dims = dimensions(bottom_rail);
    top_rail.translate(0, dims.height - rail_dims.height, 0);

    let result = join([left, right, top_rail, bottom_rail]);
    result.name = 'MiteredFrame';
    return result;
}

// **beadboard(geometry, dims)**
//
// Creates a beadboard panel suitable for use with a door frame. The offsets
// in the resulting BufferGeometry would probably make this panel unsuitable
// for use as a generic vertical panel.
export const beadboard = function(geometry, dims){
    var unit = partRemoveOffset(geometry['beadboard_unit'].clone());
    var unitDims = dimensions(unit);
    var repetitions = Math.ceil( dims.width / unitDims.width );
    var panelgeo = repeat(unit, repetitions, 0, 0);
    partScaleToFit(panelgeo, new Vector2(0, dims.height));
    panelgeo.name = 'BeadboardPanel';
    return panelgeo;
}

// **panel(geometry, dims)**
//
// Creates a 'nine slice' panel suitable for use with a door frame. This
// could be a simple flat panel, a whole single piece door panel, a raised
// panel, etc., depending on the passed geometry. This function can only
// represent panels with uniform corners, symmetrical "rails" and "stiles"
// (not literal boards, but the horizontal and vertical geometries that run
// along the edges), and a flat interior.
export const panel = function(geometry, dims){
    var result = nineSlice(
        geometry['panel_corner'].clone(),
        geometry['panel_rail'].clone(),
        geometry['panel_stile'].clone(),
        geometry['panel_center'].clone(),
        dims);
    result.name = 'Panel';
    return result;
}

export const scoopPanel = function(geometry, dims){
    var result = nineSliceWithScoop(
        geometry['panel_scoop'].clone(),
        geometry['panel_corner_tall'].clone(),
        geometry['panel_rail_tall'].clone(),
        geometry['panel_corner'].clone(),
        geometry['panel_rail'].clone(),
        geometry['panel_stile'].clone(),
        geometry['panel_center'].clone(),
        dims);
    result.name = 'Panel';
    return result;
}

// **normalizeDoor(frame, panelgeo)**
//
// After creating a frame and panel pair, the two BufferGeometry instances
// may have mismatched UV scales or origins. This method will match and
// normalize the UVs and center the panel within the frame. When you crate a
// panel for your door, you should account for the width of the frame's
// rails and stiles.
export const normalizeDoor = function(frame, panelgeo){
    centerPanelInFrame(frame, panelgeo);
    uvNormalize(frame);
    uvMatchScale(frame, panelgeo);
}

// **centerPanelInFrame(frame, panelgeo)**
//
// This is an internal function called by the `door()` function to do the
// job of aligning the panel within the X and Y extents of the frame. Only
// the panel geometry is modified. The Z offset of the object is unchanged
// (as of this writing all doors are three quarter inch and so the Z offset
// is inherent in the panel geometry).
export function centerPanelInFrame(frame, panelgeo) {
    var frameDims = dimensions(frame);
    var panelDims = dimensions(panelgeo);
    removeOffset(panelgeo);
    var dx = (frameDims.width - panelDims.width) / 2;
    var dy = (frameDims.height - panelDims.height) / 2;
    panelgeo.translate(dx, dy, 0);
}

// **frameAndPanel(geometry, dims, frameType, panelType)**
//
// This is a slightly more knowledgable function that knows which frame
// geometries belong to which IIID parametric functions. The result is
// always a two-element array like [frame, panel], but frame may be null if
// the type of door has no frame. The purpose of this function is to allow
// doors to be treated more uniformly as data rather than having lots of
// code that special cases different types. This does mean that new door
// styles may have to update this method in order to work.
//
// geometry - an Object containing the BufferGeometry instances to build the
// requested door.
// dims - the size of the door to output as a Vector2.
// panelType -
export const frameAndPanel = function(geometry, dims, frameType, panelType) {
    let frame;
    switch(frameType) {
        case null:
        case undefined:
        case "":
            frame = null;
            break;

        case 'frame_shaker_0.5in':
        case 'frame_shaker_2.5in':
            frame = buttJointFrame(geometry, dims);
            break;

        default:
            frame = miteredFrame(geometry, dims);
    }

    let panelDims;
    if(frameType) {
        const frameWidth = Number.parseFloat(
            frameType.replace(/.+_([\d.]+)*in/, '$1'));
        // Reduce the size of the panel dimensions by the frame width, but
        // add a half inch so that the panel overlaps the frame by a quarter
        // inch.
        const d = frameWidth * 2 - 0.5;
        panelDims = new Vector2(dims.x-d, dims.y-d)
    } else {
        panelDims = dims;
    }

    let panelgeo;
    switch(panelType) {
        case 'panel_beadboard':
            panelgeo = beadboard(geometry, panelDims);
            break;
        case 'panel_scoop_3x0.75':
            panelgeo = scoopPanel(geometry, panelDims);
            break;
        default:
            panelgeo = panel(geometry, panelDims);
    }

    if(frame)
        normalizeDoor(frame, panelgeo);
    else {
        uvNormalize(panelgeo);
        removeOffset(panelgeo);
    }

    return [frame, panelgeo];
}

// Light Models
// ------------

export const puckLight = function(geometry, dims) {
    var unit = partRemoveOffset(geometry['puck_light'].clone());
    return unit;
}

export const stripLight = function(geometry, dims){
    var unit = partRemoveOffset(geometry['strip_light'].clone());
    var unitDims = dimensions(unit);
    var repetitions = Math.ceil( dims.width / unitDims.width );
    var panelego = repeat(unit, repetitions, 0, 0);
    return panelgeo;
}



// Vertical Panels
// ---------------

// These functions build the vertical boring panels, corner cutbacks,
// hutches, etc.

// **verticalBoringPanel(geometry, dims)**
//
// Assembles a vertical boring panel from the components provided in the
// `geometry` parameter, which is an object containing BufferGeometry
// instances of the components. The item names that the function expects
// are:
//  * `vbp_corner`
//  * `vbp_rail`
//  * `vbp_stile`
//  * `vbp_center_plain`
//  * `vbp_center_bored`
//
// Additionally, there are optional elements of the geometry:
//  * `vbp_corner_radius3`
//  * `vbp_hutch_corner_interior_default`
//  * `vbp_hutch_corner_interior_radius3`
//
// The `dims` parameter contains the overall dimensions of the panel. The
// `boringBox` parameter is a THREE.Box2 object specifying where the boring
// holes occur.
//
// *For example,* a 24 by 36 inch panel with a column of holes placed 2
// inches from the left edge of the the 24 inch width, and another at 22
// inches (two inches from the right). The columns start four inches from
// the bottom edge and end at 32 inches, four inches from the top edge.
// ```
// var vbp = verticalBoringPanel(geometry, new Vector2(24, 36),
//         new THREE.Box2(
//              new Vector2(2, 4),
//              new Vector2(22, 32)));
// ```
//
// The options object (optional) specifies radius or cutback corners and hutch
// placement through two properties:
//
//  - `corners`: The value of this property is a list of objects. Each
//     object has the properties:
//      - `type`: A string value that is one of `radius3`, `cutback2x4`, or
//        `cutback3x6`. Any other values are treated as the default.
//      - `position`: A string value that is one of `bl`, `br`, `tl`, or
//        `tr` (case sensitive) which stand for top/bottom and left/right.
//  - `hutch`: The value of this property is another object
//      - `depth`: A floating point number indicating the height of the
//        hutch measured in inches.
//      - `height`: A floating point number indicating the height of the
//        hutch measuered in inches. This is the point on the panel where
//        it takes an L shaped turn.
//      - `type`: An optional string value that can be 'radius'. Any other
//        value (or no value) is treated as the default square corner.
//      - *Note:* instead of providing a parameter to place the hutch on the
//        left or right side of the panel, use the mirrorZ function to
//        flip the Z coordinates and thus create the symmetrical mate that
//        would pair with this boring panel. I believe this should cover
//        all cases where a left-right parameter would be used.
export function verticalBoringPanel(geometry, dims, boringBox, options){

    // 1. Create the frame
    // The corner also gives us the dimensions of the rails and stiles
    var cornerDims = dimensions(geometry.vbp_corner);

    var defaultOptions = {
        corners: [ { position: 'tl', type: 'default' },
                   { position: 'tr', type: 'default' },
                   { position: 'bl', type: 'default' },
                   { position: 'br', type: 'default' }],
        hutch: { depth: -1, height: -1, type: 'default' }
    };
    options = _extend(defaultOptions, options);

    if(options.hutch.depth > 0){
        if(options.hutch.type == 'radius3' && options.hutch.depth <= 6.0){
            console.log("WARNING: the radius3 hutch requires a minimum depth of 6.5 inches.");
            options.hutch.depth = 6.0001;
        }
        else if(options.hutch.type == 'default' && options.hutch.depth <= 0.5){
            console.log("WARNING: the default hutch requires a depth greater than 1/2 inch.");
            options.hutch.depth = 0.5001;
        }
    }

    // The default frame dimensions... any of these can be increased by a
    // radius or cutback corner. The right width can be increased even more
    // by the depth of the hutch.
    var frameDims = {
        leftWidth: cornerDims.width,
        rightWidth: cornerDims.width,
        topHeight: cornerDims.height,
        bottomHeight: cornerDims.height
    };

    var bl, tl, br, tr;
    var blDims, tlDims, brDims, trDims;

    for(var corner of options.corners){
        var geo;
        var position = corner.position;
        var cornerName = "vbp_corner_" + corner.type;
        if( geometry.hasOwnProperty(cornerName) ){
            geo = partRemoveOffset(geometry[cornerName].clone());
        }  else {
            // If it's a cutback, make it since it doesn't already exist.
            if( cornerName.includes('cutback') ){
                var matches = cornerName.match(/cutback([\d\.]+)x([\d\.]+)/);
                var width = Number.parseFloat(matches[1]);
                var height = Number.parseFloat(matches[2]);
                geo = partRemoveOffset(_vbpCutback(geometry, width, height));
                // cache this in the geometry, we may need it again.
                geo[cornerName] = geo.clone();
            } else {
                // otherwise fallback on the default
                geo = partRemoveOffset(geometry.vbp_corner.clone());
            }
        }
        var gDims = dimensions(geo);
        switch( position ){
            case 'tl':
                frameDims.topHeight = Math.max(frameDims.topHeight, gDims.height);
                tl = mirror(geo, AXIS_Y);
                tlDims = gDims;
                break;
            case 'tr':
                frameDims.topHeight = Math.max(frameDims.topHeight, gDims.height);
                tr = mirror(mirror(geo, AXIS_Y), AXIS_X);
                trDims = gDims;
                break;
            case 'bl':
                frameDims.bottomHeight = Math.max(frameDims.bottomHeight, gDims.height);
                bl = geo;
                blDims = gDims;
                break;
            case 'br':
                frameDims.bottomHeight = Math.max(frameDims.bottomHeight, gDims.height);
                br = mirror(geo, AXIS_X);
                brDims = gDims;
                break;
            default:
                console.log(`Error: unrecognized corner position, "${position}".`)
                break;
        }
    }

    var cornerFillStile = partRemoveOffset(geometry.vbp_stile.clone());
    var cornerFillCenter = partRemoveOffset(geometry.vbp_center_plain.clone());
    if( tlDims.height < frameDims.topHeight ){
        var infill = cornerFillStile.clone();
        partScaleToFit(infill, new Vector2(0, frameDims.topHeight - tlDims.height));
        var infillDims = dimensions(infill);
        if( infillDims.width < tlDims.width ){
            var infillExtra = cornerFillCenter.clone();
            partScaleToFit(infillExtra, new Vector2(tlDims.width - infillDims.width, infillDims.height));
            infill = gridJoin([[infill, infillExtra]]);
        }
        tl = gridJoin([[tl],[infill]])
        tlDims = dimensions(tl);
    }
    if( trDims.height < frameDims.topHeight ){
        var infill = cornerFillStile.clone();
        infill = mirror(infill, AXIS_X);
        partScaleToFit(infill, new Vector2(0, frameDims.topHeight - trDims.height));
        var infillDims = dimensions(infill);
        if( infillDims.width < trDims.width ){
            var infillExtra = cornerFillCenter.clone();
            partScaleToFit(infillExtra, new Vector2(trDims.width - infillDims.width, infillDims.height));
            infill = gridJoin([[infillExtra, infill]]);
        }
        tr = gridJoin([[tr], [infill]]);
        trDims = dimensions(tr);
    }
    if( blDims.height < frameDims.bottomHeight ){
        var infill = cornerFillStile.clone();
        partScaleToFit(infill, new Vector2(0, frameDims.bottomHeight - blDims.height));
        var infillDims = dimensions(infill);
        if( infillDims.width < blDims.width ){
            var infillExtra = cornerFillCenter.clone();
            partScaleToFit(infillExtra, new Vector2(blDims.width - infillDims.width, infillDims.height));
            infill = gridJoin([[infill, infillExtra]]);
        }
        bl = gridJoin([[infill],[bl]])
        blDims = dimensions(bl);
    }
    if( brDims.height < frameDims.bottomHeight ){
        var infill = cornerFillStile.clone();
        infill = mirror(infill, AXIS_X);
        partScaleToFit(infill, new Vector2(0, frameDims.bottomHeight - brDims.height));
        var infillDims = dimensions(infill);
        if( infillDims.width < brDims.width ){
            var infillExtra = cornerFillCenter.clone();
            partScaleToFit(infillExtra, new Vector2(brDims.width - infillDims.width, infillDims.height));
            infill = gridJoin([[infillExtra, infill]]);
        }
        br = gridJoin([[infill], [br]]);
        brDims = dimensions(br);
    }

    // Fix up the bottom right corner if there's a hutch.
    if( options.hutch.depth > 0 ){
        var fill = partRemoveOffset(geometry.vbp_rail.clone());
        var fillWidth = options.hutch.depth;
        partScaleToFit(fill, new Vector2(fillWidth, 0));
        var fillDims = dimensions(fill);
        if(fillDims.height < brDims.height){
            var fillCenter = partRemoveOffset(geometry.vbp_center_plain.clone());
            var fillHeight = brDims.height - fillDims.height
            partScaleToFit(fillCenter, new Vector2(fillWidth, fillHeight));
            fill = gridJoin([[fillCenter], [fill]]);
        }
        br = gridJoin([[fill, br]]);
        //brDims = dimensions(br);
    }

    // Now the rails and stiles
    var ml, mr, bc, tc;

    // the fill for the rails and stiles
    var rsFill = partRemoveOffset(geometry.vbp_center_plain.clone());

    ml = partRemoveOffset(geometry.vbp_stile.clone());
    partScaleToFit(ml, new Vector2(0, dims.height - frameDims.bottomHeight - frameDims.topHeight));

    if(options.hutch.depth > 0){
        mr = mrHutch(geometry, dims, frameDims, options.hutch);
    } else {
        mr = mirror(ml, AXIS_X);
    }

    bc = partRemoveOffset(geometry.vbp_rail.clone());
    partScaleToFit(bc, new Vector2(dims.width - blDims.width - brDims.width, 0));
    var bcDims = dimensions(bc);
    if( bcDims.height < frameDims.bottomHeight ){
        var bcFill = rsFill.clone();
        partScaleToFit(bcFill, new Vector2(bcDims.width, frameDims.bottomHeight - bcDims.height));
        bc = gridJoin([[bcFill],[bc]])
    }

    tc = mirror(partRemoveOffset(geometry.vbp_rail.clone()), AXIS_Y);
    partScaleToFit(tc, new Vector2(dims.width - tlDims.width - trDims.width, 0));
    var tcDims = dimensions(tc);
    if( tcDims.height < frameDims.topHeight ){
        var tcFill = rsFill.clone();
        partScaleToFit(tcFill, new Vector2(tcDims.width, frameDims.topHeight - tcDims.height));
        tc = gridJoin([[tc],[tcFill]]);
    }

    var frame = [[tl, tc,   tr],
                 [ml, null, mr],
                 [bl, bc,   br]];

    // 2. Create the interior with the boring holes

    // Adjust the boringBox to exclude the frame area
    var min = new Vector2(frameDims.leftWidth, frameDims.bottomHeight);
    var max = new Vector2(dims.width - frameDims.rightWidth,
                                dims.height - frameDims.topHeight);
    var validArea = new Box2(min, max);

    boringBox.intersect(validArea);

    var centerDims = dimensions(geometry.vbp_center_plain);

    var infillTemplate = geometry.vbp_center_plain.clone();
    partRemoveOffset(infillTemplate);
    partScaleToFit(infillTemplate, new Vector2(dims.width - frameDims.leftWidth - frameDims.rightWidth, 0));

    var bodyRows = [];

    var bottomFillHeight = 0;
    if (boringBox.min.y > frameDims.bottomHeight ){
        var infill = infillTemplate.clone();
        // The height of the infill is reduced by 16mm (half the centerDims
        // height) so that the boring hole is centered on the starting
        // point.
        bottomFillHeight = boringBox.min.y - frameDims.bottomHeight - centerDims.height/2;
        partScaleToFit(infill, new Vector2(0, bottomFillHeight));
        bodyRows.push(infill);
    } // if firstBox.min.y == frameDims.bottomHeight, then there's no infill

    // Note the fudge factor where we scootch the boring cells left by half
    // their width
    var leftFillWidth = boringBox.min.x - frameDims.leftWidth - centerDims.width/2;
    var centerFillWidth = boringBox.max.x - boringBox.min.x - centerDims.width;
    var rightFillWidth = dims.width - boringBox.max.x - frameDims.rightWidth - centerDims.width/2;

    var cell = geometry.vbp_center_bored.clone();
    var leftFill = partRemoveOffset(geometry.vbp_center_plain.clone());
    var centerFill = leftFill.clone();
    var rightFill = leftFill.clone();

    // Note, in some cases (but not all), this is one less repetition than I'd like
    var repetitions = Math.floor((boringBox.max.y - boringBox.min.y) / centerDims.height);

    // I'm not sure why, but it seems like sometimes repeat introduces an offset into the UVs.
    var leftCells = partRemoveOffset(repeat(cell, repetitions, 1, 1));
    var rightCells = leftCells.clone();
    var cellDims = dimensions(leftCells);

    // The elements are ordered horizontally:
    // left fill, boring cell, center fill, boring cell, right fill

    partScaleToFit(leftFill, new Vector2(leftFillWidth, cellDims.height));
    partScaleToFit(centerFill, new Vector2(centerFillWidth, cellDims.height));
    partScaleToFit(rightFill, new Vector2(rightFillWidth, cellDims.height));

    var boxGeometry = gridJoin([[leftFill, leftCells, centerFill, rightCells, rightFill]]);

    bodyRows.push(boxGeometry);

    // Make the infill above it
    var boxTopPos = boringBox.min.y + cellDims.height;
    var infill = infillTemplate.clone();
    var topFillHeight = dims.height - cellDims.height - bottomFillHeight -
        frameDims.topHeight - frameDims.bottomHeight;
    partScaleToFit(infill, new Vector2(0, topFillHeight));
    bodyRows.push(infill);

    bodyRows.reverse();
    var temp = [];
    for( var i=0; i < bodyRows.length; i++ ){
        temp.push([bodyRows[i]]);
    }
    var body = gridJoin(temp);

    frame[1][1] = body;

    var result = gridJoin(frame);
    uvNormalize(result);

    result.name = 'VerticalBoringPanel';

    return result;
}

// **mrHutch(dims, frameDims, depth, height, geometry)**
//
// This function creates the hutch geometry that takes up the middle-right
// area of the overall VerticalBoringPanel grid.
export function mrHutch(geometry, dims, frameDims, options){
    // Prepare the geometry
    var center = partRemoveOffset(geometry.vbp_center_plain.clone());
    var topStile = partRemoveOffset(geometry.vbp_stile.clone());
    topStile = mirror(topStile, AXIS_X);
    var bottomStile = topStile.clone();
    var rail = partRemoveOffset(geometry.vbp_rail.clone());
    rail = mirror(rail, AXIS_Y)
    var cornerInterior = partRemoveOffset(geometry['vbp_hutch_corner_interior_' + options.type]);
    var corner = null;
    switch( options.type ){
        case 'radius3':
            corner = partRemoveOffset(geometry.vbp_corner_radius3.clone());
            break;
        default:
            corner = partRemoveOffset(geometry.vbp_corner.clone());
            break;
    }
    corner = mirror(mirror(corner, AXIS_Y), AXIS_X);

    // Measure and scale all of the part sizes that we'll need
    var cornerDims = dimensions(corner);
    var cornerInteriorDims = dimensions(cornerInterior);
    var railDims = dimensions(rail);
    var bottomStileDims = dimensions(bottomStile);

    //var mrHeight = dims.height - frameDims.topHeight - frameDims.bottomHeight;
    var topStileHeight =  dims.height - frameDims.topHeight - options.height - cornerInteriorDims.height + railDims.height;
    var bottomStileHeight = options.height - cornerDims.height - frameDims.bottomHeight;
    var railWidth = options.depth - cornerDims.width - cornerInteriorDims.width + bottomStileDims.width;
    var centerWidth = options.depth;
    var centerHeight = bottomStileHeight;

    partScaleToFit(topStile, new Vector2(0, topStileHeight));
    partScaleToFit(bottomStile, new Vector2(0, bottomStileHeight));
    partScaleToFit(rail, new Vector2(railWidth, 0));
    partScaleToFit(center, new Vector2(centerWidth, centerHeight));

    // If corner is taller than the rail, add some fill below the interior
    // corner and the rail.
    if( railDims.height < cornerDims.height ){
        var railFill = partRemoveOffset(geometry.vbp_center_plain.clone());
        var ciFill = railFill.clone();

        var fillHeight = cornerDims.height - railDims.height;

        partScaleToFit(railFill, new Vector2(railWidth, fillHeight));
        partScaleToFit(ciFill, new Vector2(cornerInteriorDims.width, fillHeight));

        cornerInterior = gridJoin([[cornerInterior], [ciFill]]);
        rail = gridJoin([[rail], [railFill]]);
    }

    // Assemble the part
    var grid = [[topStile],
                [cornerInterior, rail, corner],
                [center, bottomStile]];
    var result = gridJoin(grid);
    return result;
}

// **_vbpCutback(geometry, width, height)**
//
// Generates an *ad hoc* cutback corner.
//
// The geometry parameter is a dictionary of BufferGeometry instances named
// `vbp_cutback_rail_endcap`, `vbp_cutback_stile_endcap`,
// `vbp_cutback_infill`, and `vbp_cutback_center`.
function _vbpCutback(geometry, width, height){
    var railEndcap = flipFlop(partRemoveOffset(geometry.vbp_cutback_rail_endcap.clone()));
    var stileEndcap = flipFlop(partRemoveOffset(geometry.vbp_cutback_stile_endcap.clone()));
    var infill = flipFlop(partRemoveOffset(geometry.vbp_cutback_infill.clone()));
    var center = flipFlop(partRemoveOffset(geometry.vbp_cutback_center.clone()));

    var railFill = infill.clone();
    var stileFill = infill.clone();

    var railEndcapDims = dimensions(railEndcap);
    var stileEndcapDims = dimensions(stileEndcap);
    var infillDims = dimensions(infill);

    var centerWidth = width - infillDims.width;
    var centerHeight = height - infillDims.height;
    var railFillWidth = centerWidth - railEndcapDims.width;
    var stileFillHeight = centerHeight - stileEndcapDims.height;

    partScaleToFit(center, new Vector2(centerWidth, centerHeight));
    partScaleToFit(railFill, new Vector2(railFillWidth, 0));
    partScaleToFit(stileFill, new Vector2(0, stileFillHeight));

    // Note that we're doing everything flip-flopped into the positive
    // quadrant because that's how gridJoin works best, but the corners are
    // modeled in the negative quadrant.
    var rail = gridJoin([[railFill, railEndcap]]);
    var stile = gridJoin([
        [stileEndcap],
        [stileFill]]);
    var result = gridJoin([
        [stile, center],
        [infill, rail]]);

    flipFlop(result);
    return result;
}

// **twoSidedVBP(geometry, dims, frontBoxes, backBoxes)**
//
// A convenience function that creates two half-thickness boring panels with
// the given boring patterns and joins them.
//
// The geometry parameter is a dictionary of BufferGeometry instances named
// `front_corner`, `front_rail`, `front_stile`, `front_center_plain`,
// `front_center_bored`, `back_corner`, `back_rail`, `back_stile`,
// `back_center_plain`, `back_center_bored`.
export function twoSidedVBP(geometry, dims, frontBox, backBox, options){
    var frontGeometry = _tsvbpSplitGeometry(geometry, 'front');
    var backGeometry = _tsvbpSplitGeometry(geometry, 'back');
    var front = verticalBoringPanel(frontGeometry, dims, frontBox, options);
    var back = verticalBoringPanel(backGeometry, dims, backBox, options);
    return join([front, back]);
}

// A subroutine to keep the twoSidedVBP implementation clean.
function _tsvbpSplitGeometry(geometry, prefix){
    var result = new Object();
    for( var name in geometry ){
        if( name.startsWith(prefix) ){
            var newname = name.replace(prefix, 'vbp');
            result[newname] = geometry[name];
        }
    }
    return result;
}

// **slatwall(geometry, dims)**
//
// The geometry parameter is a dictionary of BufferGeometry instances named
// `facing_center`, `facing_corner`, `facing_edge_bottom`,
// `facing_edge_left`, `substrate_center`, `substrate_corner`,
// `substrate_edge_bottom`, `substrate_edge_left`.
export function slatwall(geometry, dims){
    var facing = _slatwall(
        geometry['facing_center'],
        geometry['facing_corner'],
        geometry['facing_edge_bottom'],
        geometry['facing_edge_left'],
        dims
    );
    var substrate = _slatwall(
        geometry['substrate_center'],
        geometry['substrate_corner'],
        geometry['substrate_edge_bottom'],
        geometry['substrate_edge_left'],
        dims
    );

    uvNormalize(facing);
    uvNormalize(substrate);

    return [facing, substrate];
}

function _slatwall(center, corner, edge_bottom, edge_left, dims){
    var bottom = cappedRow(corner, edge_bottom, dims.width);
    var top = mirror(bottom, AXIS_Y);
    var bodyUnit = cappedRow(edge_left, center, dims.width);

    var bottomDims = dimensions(bottom);
    var bodyUnitDims = dimensions(bodyUnit);

    var repetitions = Math.floor( (dims.height - 2*bottomDims.height) / bodyUnitDims.height );
    var body = repeat(bodyUnit, repetitions, 1, 1);

    var roundedHeight = repetitions*bodyUnitDims.height + bottomDims.height;
    var newBottomHeight = dims.height - roundedHeight;

    partScaleToFit(bottom,
        new Vector2( bottomDims.width, newBottomHeight ));

    let result;
    if(repetitions == 0) {
        result = gridJoin([[top], [bottom]]);
    } else {
        result = gridJoin([[top], [body], [bottom]]);
    }
    return result;
}

// **shelfPanel(geometry, dims)**
// The geometry dictionary should be the contents of one of the
// panel_plain_*.obj files. The dims is a Vector2 with x for the width, and
// y for the depth; the height (thickness) of the shelf is baked into the
// given geometry.
export function shelfPanel(geometry, dims) {
    var panelgeo = panel(geometry, dims);
    removeOffset(panelgeo);
    uvNormalize(panelgeo);
    uvTranspose(panelgeo);

    // Rotate it before getting the dimensions sice it only measures x-y
    // dimensions and we want it's thickness (height)
    panelgeo.rotateX(Math.PI/2);
    var dims = dimensions(panelgeo);
    panelgeo.translate(0, dims.y, 0);

    return panelgeo;
}

// **shelfWire(geometries, sku, dims)**
// The geometry dictionary should contain entries for all of the SKUs for
// the set. Note that this ignores the vertical dimensions, but still uses a
// Vector2 paramters for consistency with other functions.
export function shelfWire(geometry, sku, dims) {
    var bgeo = geometry[sku];
    var bgeoDims = dimensions(bgeo);

    var repetitions = Math.floor( dims.x / bgeoDims.width );
    var result = repeat(bgeo, repetitions, 0, 0);
    var resultWidth = bgeoDims.width * repetitions;

    // Adding the spacers is a little more complex. First we decide the
    // depth of the spacer.
    var spacerZ = -1;
    switch(sku) {
        case '300':
        case '305':
            spacerZ = 12;
            break;
        case '316':
        case '37304':
            spacerZ = 10;
    }

    // If we have a spacer for this model, then we build it out of
    // a box primitive.
    if(spacerZ > 0) {
        // the shelf is 1.5 inches tall, but the spacer is about an eighth /// of an inch shy on either side.
        var spacer = new BoxGeometry(0.75, 1.25, 0.125);
        spacer.deleteAttribute('uv');
        // centered on x, moved up by half height on Y plus an eigth inch
        // margin, and out on Z to whereever the coat hanging wire is for
        // this SKU.
        spacer.translate(0.75, 0.75, spacerZ - 3/32);

        // The box geometry is indexed, but our procedures work on non-
        // indexed meshes (triangles).
        spacer = spacer.toNonIndexed();

        var offset = (resultWidth % 12.75) / 2 + 0.375;
        var spacers = [];
        var x = offset;
        // While the right edge of the spacer is less than the width of the
        // whole unit.
        while((x + 0.75) < resultWidth) {
            var offsetSpacer = spacer.clone();
            offsetSpacer.translate(x, 0, 0);
            spacers.push(offsetSpacer);
            x+=12;
        }

        spacers.push(result);
        result = join(spacers);
    }

    return result;
}

// **shelfWireTrack(geometry, dims)**
// The geometry dictionary should contain an item named 'track'
export function shelfWireTrack(geometry, dims) {
    var bgeo = geometry['track'];
    var bgeoDims = dimensions(bgeo);

    var repetitions = Math.floor( dims.y / bgeoDims.height );
    var result = repeat(bgeo, repetitions, 1, 1);

    return result;
}

// **stairs(geometry, box)**
//
// The geometry is for the tread, which will always be
// panel_plain_0.75in.obj. The dimensions are given as a THREE.Vector3 with
// the x, y, and z comporents representing width, height, and depth
// respectively. The 'steps' specifies the number of stair treads to
// generate. The top tread is always flush with the height of the bounding
// box. This function returns an array of two geometries: one for the base
// and one for the treads. This allows for a two-tone effect. The base
// doesn't  currently have UVs.
export function stairs(geometry, box, steps) {
    var width = box.x;
    var height = box.y;
    var depth = box.z;

    // The treads overhang the edge by a distance equal to their height
    var offset = 0.75;

    var baseWidth = width - 2*offset;
    var baseDepth = depth - offset;
    var baseHeight = height - offset;

    var w = baseWidth;
    var d = baseDepth / steps;
    var h = baseHeight / steps;

    var treadDepth = d + 2*offset;

    var tread = shelfPanel(geometry, new Vector2(width, treadDepth));
    tread.translate(0, 0, -offset/2);
    var treads = [];

    var vertsPerStep = 6*3;
    var p = new Float32Array(3*vertsPerStep*steps);
    var normals = new Float32Array(3*vertsPerStep*steps);

    var x0 = offset;
    var i = 0;

    // positive X (px), negative X (nx), and positive Z (pz) arrays are used
    // to set the vertex normals for the stair base
    let px = [1, 0, 0];
    let nx = [-1, 0, 0];
    let pz = [0, 0, 1];

    for(var n=1; n<=steps; n++) {
        var m = steps - n + 1;

        var clone = tread.clone();
        clone.translate(0, m*h, (n-1)*d);
        treads.push(clone);

        // The base is two triangle fans separated by the width of the unit.
        // Each triangle fan has its origin (the point to wihch all
        // triangles connect)
        var o1 = [x0,   0, 0];
        var o2 = [x0+w, 0, 0];
        var a1 = [x0,   m*h,     (n-1)*d];
        var a2 = [x0+w, m*h,     (n-1)*d];
        var b1 = [x0,   m*h,     n*d];
        var b2 = [x0+w, m*h,     n*d];
        var c1 = [x0,   (m-1)*h, n*d]
        var c2 = [x0+w, (m-1)*h, n*d]

        var triangles = [
            // the left panel of the base, if looking up the stairs
            o1, b1, a1,
            o1, c1, b1,
            // the two triangles are the front faces of the risers
            c1, b2, b1,
            c1, c2, b2,
            // the right panel of the base
            o2, a2, b2, // reverse the winding order for the opposite side
            o2, b2, c2
        ];

        let normalVectors = [
            nx, nx, nx,
            nx, nx, nx,
            pz, pz, pz,
            pz, pz, pz,
            px, px, px,
            px, px, px
        ];

        for(var j=0; j<triangles.length; j++) {
            for(var k=0; k<3; k++) {
                p[i] = triangles[j][k];
                normals[i] = normalVectors[j][k];
                i++;
            }
        }
    }

    var basegeo = new BufferGeometry();
    if(basegeo.addAttribute) {
        basegeo.addAttribute('position', new BufferAttribute(p, 3));
        basegeo.addAttribute('uv',
            new BufferAttribute(
                new Float32Array(2*vertsPerStep*steps), 2));
        basegeo.addAttribute('normal', new BufferAttribute(normals, 3));
    } else {
        basegeo.setAttribute('position', new BufferAttribute(p, 3));
        // Even though we're not doing anything with them, we need empty uv and
        // normal attributes for the join procedure.
        basegeo.setAttribute('uv',
            new BufferAttribute(
                new Float32Array(2*vertsPerStep*steps), 2));
        basegeo.setAttribute('normal', new BufferAttribute(normals, 3));
    }

    return [basegeo, join(treads)];
}

// **drawerBox(box)**
//
// Produces a drawer box with half inch walls that has the
// dimensions given by the box parameter.The origin is at the back
// left corner of the object (x,y,z map to width, height, and depth
// respectively and we're looking down the -z axis).
// `box` a THREE.Vector3 giving the width, height, and depth of the box
// `front` a boolean indicating whether to generate a front wall for the box
export function drawerBox(box, front) {
    // Width, height, depth
    let w = box.x, h = box.y, d = box.z;

    // console.log('my box: ', box);
    // console.log('width, height, depth: ', w, h, d);

     // 14 quads times six verts per quad (two triangles);
    const verts = 14*6;
    let p = new AttributeBuilder(Float32Array, 3, verts);
    let n = new AttributeBuilder(Float32Array, 3, verts);
    let uv = new AttributeBuilder(Float32Array, 2, verts);

    // Normal facing directions
    const nx = [-1, 0, 0], px = [1, 0, 0],
          ny = [0, -1, 0], py = [0, 1, 0],
          nz = [0, 0, -1], pz = [0, 0, 1];

    const wi = w-0.5; // width of the right inner surface
    const df = front ? d-0.5 : d; // depth of the front, inner surface

    // left wall, outer surface
    p.quad([0, 0, 0],
           [0, 0, d],
           [0, h, d],
           [0, h, 0]);
    n.quad(nx, nx, nx, nx);
    // wood grain on the box runs horizontally, but vertically in the
    // standard maps
    uv.quad([0, 0], [0, d], [h, d], [h, 0]);

    // left wall, inner surface
    p.rquad([0.5, 0.5, 0.5  ],
            [0.5, 0.5, df],
            [0.5, h,   df],
            [0.5, h,   0.5  ]);
    n.quad(px, px, px, px);
    uv.rquad([0.5, 0.5], [0.5, df], [h, df], [h, 0.5]);

    // left wall, upper surface
    p.quad([0,   h, 0    ],
           [0,   h, d    ],
           [0.5, h, df],
           [0.5, h, 0.5  ]);
    n.quad(py, py, py, py);
    uv.quad([0, 0], [0, d], [0.5, df], [0.5, 0.5]);

    // right wall; much the same but further to the right and with the
    // opposite winding order.
    p.rquad([w, 0, 0],
           [w, 0, d],
           [w, h, d],
           [w, h, 0]);
    n.quad(px, px, px, px);
    uv.rquad([0, 0], [0, d], [h, d], [h, 0]);

    // right wall, inner surface
    p.quad([wi, 0.5, 0.5  ],
           [wi, 0.5, df],
           [wi, h,   df],
           [wi, h,   0.5  ]);
    n.quad(nx, nx, nx, nx);
    uv.quad([0.5, 0.5], [0.5, df], [h, df], [h, 0.5]);

    // right wall, upper surface
    p.rquad([w,     h, 0    ],
            [w,     h, d    ],
            [wi, h, df],
            [wi, h, 0.5  ]);
    n.quad(py, py, py, py);
    uv.rquad([0, 0], [0, d], [0.5, df], [0.5, 0.5]);

    // back wall, outer surface
    p.quad([0, 0, 0],
           [0, h, 0],
           [w, h, 0],
           [w, 0, 0]);
    n.quad(nz, nz, nz, nz);
    uv.quad([0, 0], [h, 0], [h, w], [0, w]);

    // back wall, inner surface
    p.rquad([0.5,   0.5, 0.5],
            [0.5,   h, 0.5],
            [wi, h, 0.5],
            [wi, 0.5, 0.5]);
    n.quad(pz, pz, pz, pz);
    uv.rquad([0.5, 0.5], [h, 0.5], [h, wi], [0, wi]);

    // back wall, upper surface
    p.quad([0,     h, 0  ],
           [0.5,   h, 0.5],
           [wi, h, 0.5],
           [w,     h, 0  ]);
    n.quad(py, py, py, py);
    uv.quad([0, 0], [0.5, 0.5], [0.5, wi], [0, w]);

    // we omit the front, outer surface because it is redundant with the
    // drawer face

    if(front) {
        // front wall, inner surface
        p.quad([0.5,   0, df],
                [0.5,   h, df],
                [wi, h, df],
                [wi, 0, df]);
        n.quad(nz, nz, nz, nz);
        uv.quad([0.5, 0.5], [h, 0.5], [h, wi], [0, wi]);

		// front wall, outer surface
        p.rquad([0,   0, df],
                [0,   h, df],
                [w, h, df],
                [w, 0, df]);
        n.quad(pz, pz, pz, pz);
        uv.quad([0, 0], [h, 0], [h, w], [0, w]);

        // front wall, upper surface
        p.rquad([0,     h, d    ],
                [0.5,   h, df],
                [wi, h, df],
                [w,     h, d  ]);
        n.quad(py, py, py, py);
        uv.rquad([0, 0], [0.5, 0.5], [0.5, wi], [0, w]);
    }

    // top of the bottom
    p.quad([0.5,   0.5, 0.5  ],
           [0.5,   0.5, df],
           [wi, 0.5, df],
           [wi, 0.5, 0.5]);
    n.quad(py, py, py, py);
    uv.quad([0,0], [0, d], [w, d], [w, 0])

    // bottom of the bottom
    p.rquad([0, 0, 0],
            [0, 0, d],
            [w, 0, d],
            [w, 0, 0]);
    n.quad(ny, ny, ny, ny);
    uv.rquad([0,0], [0, d], [w, d], [w, 0])

    let geo = new BufferGeometry();
    if(geo.addAttribute) {
        geo.addAttribute('position', p.finalize());
        geo.addAttribute('normal', n.finalize());
        geo.addAttribute('uv', uv.finalize());
    } else {
        geo.setAttribute('position', p.finalize());
        geo.setAttribute('normal', n.finalize());
        geo.setAttribute('uv', uv.finalize());
    }

    uvNormalize(geo);

    geo.name = 'DrawerBox';

    return geo;
}

// High level assembly
// -------------------

// These functions help to assemble frames, panels, rails, and stiles from
// the provided components.

// **nineSlice(corner, edge_bottom, edge_left, center, dims)**
//
// Implements a nine-slice so that the result has smooth UVs across the
// surface with no visible disjunctions. If the nine slices are like the
// nine keys on the numeric keypad of a a computer keyboard, then the four
// source components are numbered 1, 2, 4, 5 and processed in that order.
// The dims parameter should be a Vector2 with width and height
// properties.
export function nineSlice(corner, edge_bottom, edge_left, center, dims){
  var corner_dims = dimensions(corner);
  if(corner.boundingBox == null) corner.computeBoundingBox();
  var corner_bounds = corner.boundingBox; // this may get overwritten later
  var offset_x = corner_bounds.min.x;
  var offset_y = corner_bounds.min.y;
  var corner_uvBounds = uvBounds(corner);
  var center_dims = dimensions(center);

  // We scale the center piece so that it takes up the full width of the
  // panel minus both end caps. The same vertical and horizontal scale is
  // used to change the size of the "rails" and "stiles" as well as the
  // center part of the panel.
  var new_center_width = dims.width - 2*corner_dims.width;
  var new_center_height = dims.height - 2*corner_dims.height;
  var scale_horizontal = new_center_width / center_dims.width;
  var scale_vertical = new_center_height / center_dims.height;

  [corner, edge_bottom, edge_left, center].forEach(function(item){
    removeOffset(item);
    uvRemoveOffset(item);
  });

  // The partScale function both scales the vertex positions and the UVs
  // at the same time, avoiding the UV distortion.
  partScale(edge_bottom, new Vector2(scale_horizontal, 1));
  partScale(edge_left, new Vector2(1, scale_vertical));
  var edge_right = mirror(edge_left, AXIS_X);
  var edge_top = mirror(edge_bottom, AXIS_Y);
  partScale(center, new Vector2(scale_horizontal, scale_vertical))

  var corner_bottom_left = corner;
  var corner_bottom_right = mirror(corner, AXIS_X);
  var corner_top_left = mirror(corner, AXIS_Y);
  var corner_top_right = mirror(corner_top_left, AXIS_X)

  // Joining all the pieces in a grid means that the UVs are offset by the
  // width and height of each part, keeping their seams aligned so we get
  // one smooth piece as a result.
  var result = gridJoin(
    [[corner_top_left,    edge_top,    corner_top_right],
     [edge_left,          center,      edge_right      ],
     [corner_bottom_left, edge_bottom, corner_bottom_right ]]);

  // We preserve the original offset of the input geometry, although mostly
  // this  offset is probably zero. The result.translate method is provided
  // by three.js's BufferGeometry class, and the uvTranslate function is the
  // equivalent operation defined in this file.
  result.translate(offset_x, offset_y, 0)
  uvTranslate(result, new Vector2(corner_uvBounds.min.u, corner_uvBounds.min.v));

  return result;
}

export function nineSliceWithScoop(scoop, corner_tall, rail_tall, corner, edge_bottom, edge_left, center, dims) {
    var scoop_dims = dimensions(scoop);
    var corner_tall_dims = dimensions(corner_tall);
    var rail_tall_dims = dimensions(rail_tall);

    var corner_dims = dimensions(corner);
    if(corner.boundingBox == null) corner.computeBoundingBox();
    var corner_bounds = corner.boundingBox;
    var offset_x = corner_bounds.min.x;
    var offset_y = corner_bounds.min.y;
    var corner_uvBounds = uvBounds(corner);
    var center_dims = dimensions(center);

    var scale_scoop = 1;
    var scale_scoop_rail = 0;
    if ((scoop_dims.width + 2 * corner_tall_dims.width) > dims.width) {
        scale_scoop = dims.width / (scoop_dims.width + 2 * corner_tall_dims.width);
    } else {
        scale_scoop_rail = (dims.width - (scoop_dims.width + 2 * corner_tall_dims.width)) / (rail_tall_dims.width * 2);
    }

    var new_center_width = dims.width - 2 * corner_dims.width;
    var new_center_height = dims.height - corner_dims.height - scoop_dims.height * scale_scoop;
    var scale_horizontal = new_center_width / center_dims.width;
    var scale_vertical = new_center_height / center_dims.height;

    [scoop, corner_tall, rail_tall, corner, edge_bottom, edge_left, center].forEach(function(item){
      removeOffset(item);
      uvRemoveOffset(item);
    });

    partScale(scoop, new Vector2(scale_scoop, scale_scoop));
    partScale(corner_tall, new Vector2(scale_scoop, scale_scoop));
    partScale(rail_tall, new Vector2(scale_scoop_rail, 1));
    var rail_tall_2 = mirror(rail_tall, AXIS_X);
    partScale(edge_bottom, new Vector2(scale_horizontal, 1));
    partScale(edge_left, new Vector2(1, scale_vertical));
    var edge_right = mirror(edge_left, AXIS_X);
    partScale(center, new Vector2(scale_horizontal, scale_vertical))

    var corner_bottom_left = corner;
    var corner_bottom_right = mirror(corner, AXIS_X);

    var corner_top_left = mirror(corner_tall, AXIS_NONE);
    var corner_top_right = mirror(corner_top_left, AXIS_X);

    var result = gridJoin(
      [[corner_top_left,    rail_tall,  scoop,  rail_tall_2,    corner_top_right],
       [edge_left,          center,      edge_right      ],
       [corner_bottom_left, edge_bottom, corner_bottom_right ]]);

    result.translate(offset_x, offset_y, 0)
    uvTranslate(result, new Vector2(corner_uvBounds.min.u, corner_uvBounds.min.v));

    return result;
  }

// **rail(geometry, length)**
//
// Create a rail of the given length from the endcap and the body geometry.
export function rail(geometry, length){
  var start = geometry['frame_rail_endcap'].clone();
  var body = geometry['frame_rail_body'].clone();
  var end = geometry['frame_rail_endcap'].clone();

  // normally we would do a mirror operation, but we need a different UV
  // orientation for rails (horizontal frame members).
  var dims = dimensions(end);
  var dims_uv = uvDimensions(end);
  end.scale(-1,1,1);
  end.translate(dims.width, 0, 0);
  flipNormals(end);
  uvScale(end, new Vector2(1, -1));
  uvTranslate(end, new Vector2(0, dims_uv.height));

  var start_dims = dimensions(start);
  var body_dims = dimensions(body)

  var new_body_width = length - 2*start_dims.width;
  var scale = new_body_width / body_dims.width;

  body.scale(scale, 1, 1);
  // note that we're scaling the UVs vertically since the wood grain runs in the
  // long direction of the rail.
  uvScale(body, new Vector2(1, scale));

  return rail_join([start, body, end]);
}

// **stile(geometry, length)**
//
// Create a stile of the given length from the endcap and body geometry.
export function stile(geometry, length){
  var start = geometry['frame_stile_endcap'].clone();
  var body = geometry['frame_stile_body'].clone();
  var end = mirror(start, AXIS_Y);

  var start_dims = dimensions(start)
  var body_dims = dimensions(body)

  var new_body_height = length - 2*start_dims.height;
  var scale = new_body_height / body_dims.height;

  body.scale(1, scale, 1);
  uvScale(body, new Vector2(1, scale));

  return stile_join([start, body, end]);
}

// **cappedRow(endcap, body, length)**
//
// Mirrors the endcap and scales the body to fill the given length.
export function cappedRow(endcap, body, length){
    let start = partRemoveOffset(endcap.clone());
    let end = mirror(start, AXIS_X);
    body = partRemoveOffset(body.clone());

    let startDims = dimensions(start)
    let newBodyWidth = length - 2*startDims.width;

    partScaleToFit(body, new Vector2(newBodyWidth, 0));

    return gridJoin([[start, body, end]]);
}

// UV operations
// -------------

// These functions change the UV coordinates of BufferGeometry, which are
// stored in its 'uv' attribute.

// **uvBounds(bgeo)**
//
// Finds the minimum and maximum `u` and `v` values for the given BufferGeometry.
// Returns a Box2 object with properties min and max, which are Vector2
// objects. So if bounds = uvBounds(bgeo), then the left bound is given by
// bounds.min.x, while the top bound is given by bounds.max.y. The parameter
// bgeo should be a BufferGeometry instance.
export function uvBounds(bgeo){
if(bgeo.userData['uvNeedsUpdate'] !== false){
    var u_min, v_min, u_max, v_max;
    u_min = v_min = Infinity;
    u_max = v_max = -Infinity;

    var uvs = bgeo.getAttribute('uv');
    if(uvs == null) return new Vector2(0, 0);

    for( var i=0; i<uvs.array.length; i+=2 ){
        var u = uvs.array[i];
        var v = uvs.array[i+1];
        if( u < u_min ) u_min = u;
        if( u > u_max ) u_max = u;
        if( v < v_min ) v_min = v;
        if( v > v_max ) v_max = v;
    }
    let bounds = new Box2(
        new Vector2(u_min, v_min),
        new Vector2(u_max, v_max));
        bgeo.userData['uvBounds'] = bounds;
        bgeo.userData['uvNeedsUpdate'] = false;
    }
    return bgeo.userData['uvBounds']
}

// Return the width and height of the bmesh's UVs as a Vector2 with
// properties named width and height. The parameter bgeo should be a
// BufferGeometry instance.
export function uvDimensions(bgeo){
    // b is a Box2 with min and max properties, each of which are Vector2 instances.
    var b = uvBounds(bgeo);
    return new Vector2(b.max.x - b.min.x, b.max.y - b.min.y);
}

// **uvScale(bgeo, scale)**
//
// Scales the UV coordinates by the given scale factor. The bgeo should be a
// BufferGeometry instance. The scale parameter should be a Vector2 where x
// and y correspond to the u and v scaling factors.
export function uvScale(bgeo, scale){
    var uvs = bgeo.getAttribute('uv');
    for( var i=0; i<uvs.array.length; i+=2 ){
        var u = i; var v = i+1;
        uvs.array[u] *= scale.x
        uvs.array[v] *= scale.y
    }
    uvs.needsUpdate = true;
    bgeo.userData['uvNeedsUpdate'] = true;
}

// **uvTranspose(bgeo, mi)**
//
// Flips the UVs along a 45 degree line passing through the origin by
// switching the x and y coordinates. This is slightly less computationally
// expensive than a true 90 degree rotation, and can be "undone" simply by
// applying the procedure a second time.
//
// The `mi` parameter allows you to specify a material index
export function uvTranspose(bgeo, mi=-1){
    var uvs = bgeo.getAttribute('uv');
    if( mi > -1 ){
        for( var group of bgeo.groups ){
            if( group.materialIndex == mi ){
                var i = group.start * uvs.itemSize;
                var imax = (i + group.count) * uvs.itemSize;
                _uvTranspose(uvs.array, i, imax);
            }
        }
    } else {
        _uvTranspose(uvs.array, 0, uvs.array.length);
    }
    uvs.needsUpdate = true;
    bgeo.userData['uvNeedsUpdate'] = true;
}

function _uvTranspose(a, i, imax){
    while( i < imax ) {
        var u=i;
        var v=i+1;
        [a[u], a[v]] = [a[v], a[u]];
        i+=2;
    }
}

// **uvTranslate(bgeo, translation)**
//
// Translates the UV coordinates by the given translation factor. The bgeo
// should be a BufferGeometry instance. The translation parameter should be a
// Vector2.
export function uvTranslate(bgeo, translation){
    var uvs = bgeo.getAttribute('uv');
    for( var i=0; i<uvs.array.length; i+=2 ){
        var u = i; var v = i+1;
        uvs.array[u] += translation.x
        uvs.array[v] += translation.y
    }
    uvs.needsUpdate = true;
    bgeo.userData['uvNeedsUpdate'] = true;
}

// **uvRemoveOffset(bgeo)**
//
// Subtracts the minimum UV values from all points, thus placing the lower left
// corner of the bounding box at the origin of the UV square. The bgeo should be
// a BufferGeometry instance.
export function uvRemoveOffset(bgeo){
    var bounds = uvBounds(bgeo)
    var offset = bounds.min
    var uvs = bgeo.getAttribute('uv');
    for( var i=0; i<uvs.array.length; i+=2 ){
        var u = i; var v = i+1;
        uvs.array[u] -= offset.x
        uvs.array[v] -= offset.y
    }
    uvs.needsUpdate = true;
    bgeo.userData['uvNeedsUpdate'] = true;
}

// **uvNormalize(bgeo)**
//
// Given a BufferGeometry, scale the UV coordinates so that they fit within
// one repetition of the texture. This amounts to dividing all of the UV
// coordinates by the the width or height, whichever is larger.
export function uvNormalize(bgeo){
    var uvDims = uvDimensions(bgeo);
    var scale = 1 / Math.max(uvDims.width, uvDims.height)
    uvScale(bgeo, new Vector2(scale, scale));
}

// **measureUVScale(bgeo)**
//
// Measures the scale of the BufferGeometry's UVs by comparing the length of
// the XYZ edges to the correspondin UV edges. The BufferGeometry is assumed
// to be non-indexed triangles. The units of the returned value are UV unit
// per inch, so you can multiply the result by the pixel dimensions of the
// UV map to calculate a pixels per inch (PPI) value for the texture map.
export function uvMeasureScale(bgeo){
    var p1 = new Vector3();
    var p2 = new Vector3();
    var p3 = new Vector3();
    var uv1 = new Vector2();
    var uv2 = new Vector2();
    var uv3 = new Vector2();

    var pos = bgeo.getAttribute('position');
    var uvs = bgeo.getAttribute('uv');
    var i, j;
    var sPos = 0;
    var sUV = 0;
    for( i=0, j=0; i < pos.count; i+=9, j+=6 ){
        p1.set(pos.array[i], pos.array[i+1], pos.array[i+2]);
        p2.set(pos.array[i+3], pos.array[i+4], pos.array[i+5]);
        p3.set(pos.array[i+6], pos.array[i+7], pos.array[i+8]);
        uv1.set(uvs.array[j], uvs.array[j+1]);
        uv2.set(uvs.array[j+2], uvs.array[j+3]);
        uv3.set(uvs.array[j+4], uvs.array[j+5]);
        sPos += p1.distanceTo(p2);
        sPos += p2.distanceTo(p3);
        sPos += p3.distanceTo(p1);
        sUV += uv1.distanceTo(uv2);
        sUV += uv2.distanceTo(uv3);
        sUV += uv3.distanceTo(uv1);
    }
    return sUV / sPos;
}

// **uvMatchScale(reference, subject)**
//
// Measures the UV scale of the reference and subject by comparing the
// length of the geometry's XYZ edges to the corresponding UV edge. The UVs
// of the subject are then scaled to match the reference.
export function uvMatchScale(reference, subject){
    var ref = uvMeasureScale(reference);
    var sub = uvMeasureScale(subject);
    var scale = ref/sub;
    // If the subject is smaller on the UV plane than the reference, then
    // `ref/sub` is greater than one, and we will scale the subject to be
    // larger. In the opposite case, the subject is larger than the
    // reference, we will have a `ref/sub` value that is less than one, so
    // the subject's UVs will be shrunk.
    uvScale(subject, new Vector2(scale, scale));
}

//  BufferGeometry operations
// -----------------------------

// **removeOffset(bgeo)**
//
// Subtracts the minimum X and Y values from all vertices, thus placing the
// lower left corner of the bounding box at the local origin of the mesh.
export function removeOffset(bgeo){
  if(bgeo.boundingBox == null) bgeo.computeBoundingBox();
  let offset = bgeo.boundingBox.min
  bgeo.translate(-offset.x, -offset.y, 0);
}

// **leftOffset(bego)**
//
// Find the left offset of the given BufferGeometry, i.e. its minimum x axis
// coordinate..
export function leftOffset(bgeo){
    if(bgeo.boundingBox == null) bgeo.computeBoundingBox();
    return bgeo.boundingBox.min.x
}

// **bottomOffset(bego)**
//
// Find the bottom offset of the given BufferGeometry, i.e. its minimum y
// axis coordinate..
export function bottomOffset(bgeo){
    if(bgeo.boundingBox == null) bgeo.computeBoundingBox();
    return bgeo.boundingBox.min.y
}

// **dimensions(bgeo)**
//
// Return the width and height of the BufferGeometry as a Vector2.
export function dimensions(bgeo){
  if(bgeo.boundingBox == null) bgeo.computeBoundingBox();
  return new Vector2(
    bgeo.boundingBox.max.x - bgeo.boundingBox.min.x,
    bgeo.boundingBox.max.y - bgeo.boundingBox.min.y);
}

// **flipNormals(bgeo)**
//
// Changes the winding direciton of the triangles in the BufferGeometry. Assumes
// that the positions are all triangles and that the UVs and Normals are aligned
// to the triangles. It blows my mind that this worked on the first try.
export function flipNormals(bgeo){
  for( var key in bgeo.attributes ){
    var attr = bgeo.getAttribute(key);
    var size = attr.itemSize; // 3 for position/normal, 2 for UV

    var tri, vert, i, temp;

    // tri is the index of the triangle we're working on, so we jump through the
    // array in bounds of three times the size of the attribute
    for( tri=0; tri < attr.array.length; tri += size*3 ){
      // Fill the buffer with temporaries from position 0
      for( i=0; i<size; i++ ){
        temp = attr.array[tri+i];
        attr.array[tri+i] = attr.array[tri+2*size+i];
        attr.array[tri+2*size+i] = temp;
      }
    }
    attr.needsUpdate = true;
  }
}

// **repeat(bgeo, repetitions, axis, axis_uv)**
//
// Like a simplified version of Blender's array modifier, this function
// repeats bgeo in the positive X or Y direction and repeats its UVs in the
// positive U or V directions. The axis of repetition is set separately for
// the verticies and the UVs to accomodate rails, which are horizontal in 3D
// space, but vertical in UV space.
export function repeat(bgeo, repetitions, axis, axis_uv){
  var dims = dimensions(bgeo);
  var dims_uv = uvDimensions(bgeo);

  var result = new BufferGeometry();

  for( var key in bgeo.attributes ){
    var attr = bgeo.getAttribute(key);
    var old_size = attr.count * attr.itemSize;
    var new_size =  old_size * repetitions;
    var data = new Float32Array(new_size);

    for( var i=0; i < repetitions; i++ ){
      data.set(attr.array, i * old_size );
    }

    if(result.addAttribute)
        result.addAttribute(key, new BufferAttribute(data, attr.itemSize));
    else
        result.setAttribute(key, new BufferAttribute(data, attr.itemSize));
  }

  // Iterate over just x or y. This may be fairly fast because we don't do any
  // multiplication in the loops.
  var position = result.getAttribute('position');
  var stride = Math.round((position.count * position.itemSize) / repetitions);

  if( axis == 0 ){
    var x = 0; var k = 0;
    for( var i=0; i < repetitions; i++ ){
      for( var j=0; j < stride; j+=3 ){
        position.array[k] += x;
        k += 3; // k = i * stride + j
      }
      x += dims.width;
    }
  } else if( axis == 1 ){
    var y = 0; var k = 1;
    for( var i=0; i < repetitions; i++ ){
      for( var j=1; j < stride; j+=3 ){
        position.array[k] += y;
        k += 3; // k = i * stride + j
      }
      y += dims.height;
    }
  }
  position.needsUpdate = true;

  // Iterate over just u or just v by jumping by twos
  var uv = result.getAttribute('uv');
  if(uv) {
      stride = Math.round((uv.count * uv.itemSize) / repetitions);

      if( axis_uv == 0 ){
        var u = 0; var k = 0;
        for( var i=0; i < repetitions; i++ ){
          for( var j=0; j < stride; j+=2 ){
            uv.array[k] += u;
            k += 2; // k = i * stride + j
          }
          u += dims_uv.width;
        }
      } else if( axis_uv == 1 ){
        var v = 0; var k = 1;
        for( var i=0; i < repetitions; i++ ){
          for( var j=1; j < stride; j+=2 ){
            uv.array[k] += v;
            k += 2; // k = i * stride + j
          }
          v += dims_uv.height;
        }
      }
      uv.needsUpdate = true;
  }
  return result;
}

// **join(geometries)**
//
// Given an array of BufferGeometry instances, this creates a new
// BufferGeometry instance with all the data concatenated together.
//
// A few assumptions make this fast and simple, but not completely general:
// * the BufferGeometries are not indexed
// * there's no morphing attributes
// * all the geometries have the same attributes of the same size
export function join(geometries){
    var result = new BufferGeometry();

    // Loop through the attribute names, usually position, uv, and normal
    for( var key in geometries[0].attributes ){
        var size=0;
        // Sum the sizes of all the geometries
        for( var i=0; i< geometries.length; i++ ){
            var attr = geometries[i].getAttribute(key);
            size += attr.array.length;
        }
        // Make an array for this attribute long enough for all the geometry
        // data.
        var data = new Float32Array(size);
        // Fill the geometry data; the `offset` variable keeps track of the
        // current position in the output array
        var offset=0;
        for( var i=0; i<geometries.length; i++ ){
            var attr = geometries[i].getAttribute(key);
            data.set(attr.array, offset);
            offset += attr.array.length;
        }
        // Finally put the data into the return value
        if(result.addAttribute)
            result.addAttribute(key, new BufferAttribute(data, attr.itemSize));
        else
            result.setAttribute(key, new BufferAttribute(data, attr.itemSize));
    }
    return result;
}

//  Changing UV/Geometry Simultaneously
// ------------------------------------

// The "parts" of a parametric object are the components that are duplicated
// and shifted to create a finished piece. These operations act on the
// vertex geometry and UV coordinates of parts in one single function call.

// **mirror(bgeo, axis)**
//
// Create a mirrored copy of a part, flipping UVs as necessary. This assumes
// that the part's origin is at 0,0 and the vertecies/UVs have no offset. The
// original BufferGeometry is unchanged.

export const AXIS_X = 0;
export const AXIS_Y = 1;
export const AXIS_NONE = -1;

export function mirror(bgeo, axis){
  var dims = dimensions(bgeo);
  var dims_uv = uvDimensions(bgeo);

  var result = bgeo.clone();

  if(axis == AXIS_X){
    result.scale(-1, 1, 1);
    result.translate(dims.width, 0, 0);
    flipNormals(result);
    uvScale(result, new Vector2(-1, 1));
    uvTranslate(result, new Vector2(dims_uv.width, 0));
  } else if(axis == AXIS_Y) {
    result.scale(1, -1, 1);
    result.translate(0, dims.height, 0);
    flipNormals(result);
    uvScale(result, new Vector2(1, -1));
    uvTranslate(result, new Vector2(0, dims_uv.height));
  } else if(axis == AXIS_NONE){
  } else {
    console.log("Error IIID.js:mirror(): axis should be 0 or 1.");
  }

  return result
}

// **flipFlop(bgeo)**
//
// Mirror on both X and Y axes simultaneously. Modifies the geometry in
// place.
export function flipFlop(bgeo){
    dims = dimensions(bgeo);
    uvDims = uvDimensions(bgeo);
    bgeo.scale(-1, -1, 1);
    bgeo.translate(dims.width, dims.height, 0);
    flipNormals(bgeo);
    uvScale(bgeo, new Vector2(-1, -1));
    uvTranslate(bgeo, new Vector2(uvDims.width, uvDims.height));

    // The input value is modified in place and returned for convenient
    // chaining.
    return bgeo;
}

// **function mirrorZ(bgeo)**
//
// This function crates a mirrored copy of the part in the Z dimension. This
// is useful for creating a matching boring panel with exactly the same
// pattern of holes on the opposite face of the panel. The part is offset
// in the Z direction so that its origin is in the same position relative
// to the depth of the panel.
export function mirrorZ(bgeo){
    if(bgeo.boundingBox == null) bgeo.computeBoundingBox();
    var depth = bgeo.boundingBox.max.z - bgeo.boundingBox.min.z;

    result = bgeo.clone();

    result.scale(1, 1, -1);
    result.translate(0, 0, depth);
    flipNormals(result);
    return result;
}

// **partScale(bgeo, scale)**
//
// Simultaneously scales the vertices of the mesh and the UV coordinates.
// The BufferGeometry is changed in place. The scale parameter should be a
// Vector2. Note that scaling the X and Y coordinates in the same direction
// as the U and V coordinates is onle useful in some cases... often the
// scaling is in opposite direcitons.
export function partScale(bgeo, scale){
  bgeo.scale(scale.x, scale.y, 1);
  uvScale(bgeo, scale);
}

// **partScaleToFit(bgeo, size)**
//
// Given a width and height, scale the BufferGeometry and its UVs to fit
// into width and height. Use a zero value for the width or height parameter
// to indicate no change on that axis. This method does not accomodate
// negative scaling.
export function partScaleToFit(bgeo, size){
    var dims = dimensions(bgeo);
    var scaleX = 1;
    var scaleY = 1;
    if( size.width > 0 )
        scaleX = size.width / dims.width;
    if( size.height > 0 )
        scaleY = size.height / dims.height;
    partScale(bgeo, new Vector2(scaleX, scaleY));
}

// **partRemoveOffset(bgeo)**
//
// Removes the offset from the vertexes and the UVs simultaneously. Returns
// the input parameter for convenience with chaining.
export function partRemoveOffset(bgeo){
    removeOffset(bgeo);
    uvRemoveOffset(bgeo);
    return bgeo;
}

// **gridJoin(grid)**
//
// The grid is joined into a new result. Write the grid as a list of rows;
// the rows being lists of bmeshes. Write it so that it looks
// "right-side-up" as written, but remember that when assembled the geometry
// will be in the positive quadrant of the XY plane. If the bottom-left
// corner has a UV or XY offset, then that offset will be preserved. The XY
// and UV coordinates of the grid items will be modified in-place, so they
// can't be reused.
//
// Here's how you would write the grid for a 3x3:
//    [[left-top,    center-top,    right-top   ],
//     [left-center, center-center, right-center],
//     [left-bottom, center-bottom, right-bottom]]
//
export function gridJoin(grid){
  var x, y, u, v
  x = y = 0; u = v = 0;
  for( var i=grid.length-1; i >= 0; i-- ){
    var row = grid[i];
    x = 0; u = 0;
    var new_y, new_v;
    var first_in_row = true;
    for( var j=0; j < row.length; j++ ){
      var item = row[j];
      var dims = dimensions(item);
      var dims_uv = uvDimensions(item);
      if( first_in_row ){
        new_y = y + dims.height;
        new_v = v + dims_uv.height;
        first_in_row = false;
      }
      item.translate(x, y, 0);
      uvTranslate(item, new Vector2(u, v));
      x = x + dims.width;
      u = u + dims_uv.width;
    }
    y = new_y;
    v = new_v;
  }
  var flat = new Array();
  for(var i=0; i<grid.length; i++){
    row = grid[i];
    for(var j=0; j<row.length; j++){
      flat.push(row[j]);
    }
  }

  return join(flat);
}

// **rail_join(rail)**
//
// A rail join concatenates the BufferGeometry instances in the `rail` list into
// a new BufferGeometry instance. Each item is moved horizontally in 3D space,
// but its UVs are moved vertically in UV space. This is to maintain the grain
// direction in the long direction of the rail. The geometry of the items in the
// rail list are modified in place so they can't be reused.
export function rail_join(rail){
  var results = Array();
  var x, v; x = v = 0;
  for( var i=0; i<rail.length; i++ ){
    var item = rail[i];
    var dims = dimensions(item);
    var dims_uv = uvDimensions(item);

    item.translate(x, 0, 0);
    uvTranslate(item, new Vector2(0, v));
    results.push(item);

    x += dims.width;
    v += dims_uv.height;
  }
  return join(results);
}

// **stile_join(stile)**
//
// A stile join concatenates the BufferGeometry in the `stile` list into a new
// BufferGeometry instance. Each item is moved vertically in both 3D space and
// in UV space. This maintains the grain direction. The geometries are modified
// in place and can't be reused.
export function stile_join(stile){
  var results = Array();
  var y, v; y = v = 0;

  for( var i=0; i<stile.length; i++){
    var item = stile[i];
    var dims = dimensions(item);
    var dims_uv = uvDimensions(item);

    item.translate(0, y, 0)
    uvTranslate(item, new Vector2(0, v));
    results.push(item);

    y += dims.height;
    v += dims_uv.height;
  }
  return join(results);
}

//  Miscellaneous
// --------------

// **_extend(obj, props)**
//
// Override the properties in obj with the properties in props. This only
// works for plain objects and their enumerable properties, not like the
// underscore.js version, which also works with class members and methods.
function _extend(obj, props){
    for(let prop in props){
        if( (typeof obj[prop]) == 'object' ){
            _extend(obj[prop], props[prop]);
        } else {
            obj[prop] = props[prop];
        }
    }
    return obj;
}

